import { brandPalette } from "../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  FormLabel,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../components/Models/admin-modal-theme";
import { examSubmissionPdfUrl } from "../../../components/student/PdfPreviewModal";
import HomeworkPdfAnnotator from "../reports/homework-pdf-annotator";
import {
  clearCorrectionQuestions,
  correctStudentExamRequest,
  getCorrectionQuestionsRequest,
} from "../../../modules/admin-modules/student-exams/Actions";
import { getStudentExamFile } from "../../../modules/admin-modules/student-exams/Api";
import { toast } from "react-toastify";

const QUESTION_IMAGE_BASE = "https://edutopiaonline.com/api";

async function fetchExamPdfBytes(filename) {
  const response = await getStudentExamFile(filename);
  return response.data;
}

function hasSubmittedPdf(studentExam) {
  return studentExam?.student_pdf != null && String(studentExam.student_pdf).trim() !== "";
}

function getQuestionId(item) {
  return item?.question_id ?? item?.question?.id;
}

function getQuestionData(item) {
  return item?.question ?? item;
}

const CorrectExamModal = ({ isOpen, onClose, studentExam, examId }) => {
  const dispatch = useDispatch();
  const correctionQuestions = useSelector((state) => state.adminStudentExams.correctionQuestions);
  const isQuestionsLoading = useSelector((state) => state.adminStudentExams.isQuestionsLoading);
  const isSubmittingCorrection = useSelector((state) => state.adminStudentExams.isSubmittingCorrection);

  const [corrections, setCorrections] = useState({});
  const [annotatedFile, setAnnotatedFile] = useState(null);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !studentExam?.id) return;
    dispatch(getCorrectionQuestionsRequest(studentExam.id));
  }, [isOpen, studentExam?.id, dispatch]);

  useEffect(() => {
    if (!isOpen || !correctionQuestions?.length) {
      setCorrections({});
      return;
    }
    const initial = {};
    correctionQuestions.forEach((item) => {
      const qid = getQuestionId(item);
      if (qid == null) return;
      initial[qid] = { mark: "", comment: "" };
    });
    setCorrections(initial);
  }, [isOpen, correctionQuestions]);

  const handleClose = useCallback(() => {
    setCorrections({});
    setAnnotatedFile(null);
    setAnnotatorOpen(false);
    dispatch(clearCorrectionQuestions());
    onClose();
  }, [dispatch, onClose]);

  const updateField = useCallback((questionId, field, value) => {
    setCorrections((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      },
    }));
  }, []);

  const studentLabel = useMemo(() => {
    const name = studentExam?.student?.name;
    const code = studentExam?.student?.user_name;
    if (name && code) return `${name} (${code})`;
    return name || code || "Student";
  }, [studentExam]);

  const studentPdfFilename = studentExam?.student_pdf;
  const pdfUrl = hasSubmittedPdf(studentExam) ? examSubmissionPdfUrl(studentPdfFilename) : "";

  const handleSubmit = () => {
    if (!studentExam?.id) return;

    const payloadCorrections = [];
    for (const item of correctionQuestions) {
      const questionId = getQuestionId(item);
      const question = getQuestionData(item);
      const maxMark = Number(question?.mark) || 0;
      const entry = corrections[questionId] ?? {};
      const markRaw = entry.mark;
      const mark = markRaw === "" || markRaw == null ? NaN : Number(markRaw);

      if (!Number.isFinite(mark) || mark < 0 || mark > maxMark) {
        toast.error(`Enter a valid mark (0–${maxMark}) for each question.`);
        return;
      }

      payloadCorrections.push({
        question_id: questionId,
        mark,
        comment: entry.comment ?? "",
      });
    }

    if (!payloadCorrections.length) {
      toast.error("No questions to correct.");
      return;
    }

    const basePayload = {
      student_exam_id: studentExam.id,
      student_id: studentExam.student_id ?? studentExam.student?.id,
      corrections: payloadCorrections,
      exam_id: examId,
    };

    if (annotatedFile) {
      const formData = new FormData();
      formData.append("student_exam_id", basePayload.student_exam_id);
      formData.append("student_id", basePayload.student_id);
      if (basePayload.exam_id != null) formData.append("exam_id", basePayload.exam_id);
      basePayload.corrections.forEach((correction, index) => {
        formData.append(`corrections[${index}][question_id]`, correction.question_id);
        formData.append(`corrections[${index}][mark]`, correction.mark);
        formData.append(`corrections[${index}][comment]`, correction.comment ?? "");
      });
      formData.append("student_pdf", annotatedFile);
      dispatch(correctStudentExamRequest({ formData }, handleClose));
      return;
    }

    dispatch(correctStudentExamRequest(basePayload, handleClose));
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        {...adminModalContentProps}
        maxH="90vh"
        bg="var(--surface-raised)"
        color="var(--text-primary)"
      >
        <ModalHeader {...adminModalHeaderProps}>Correct exam — {studentLabel}</ModalHeader>
        <ModalCloseButton color="var(--text-primary)" />
        <ModalBody {...adminModalBodyProps}>
          {hasSubmittedPdf(studentExam) ? (
            <HStack justify="flex-end" flexWrap="wrap" spacing={2} mb={6} dir="ltr">
              <Button
                as="a"
                href={pdfUrl}
                target="_blank"
                size="sm"
                borderRadius="lg"
                bg="var(--surface-muted)"
                border="1px solid"
                borderColor="var(--border-subtle)"
                color="var(--text-primary)"
                fontWeight="800"
                _hover={{ bg: "var(--surface-raised)", borderColor: brandPalette.primary }}
              >
                Open submission
              </Button>
              <Button
                size="sm"
                borderRadius="lg"
                bg={brandPalette.primary}
                color="white"
                fontWeight="800"
                _hover={{ bg: brandPalette.secondary }}
                onClick={() => setAnnotatorOpen(true)}
              >
                Annotate PDF
              </Button>
              {annotatedFile ? (
                <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                  Annotated PDF ready
                </Badge>
              ) : null}
            </HStack>
          ) : null}
          {isQuestionsLoading ? (
            <VStack py={12}>
              <Spinner size="lg" color={brandPalette.primary} />
              <Text fontSize="sm" color="var(--text-muted)">
                Loading questions…
              </Text>
            </VStack>
          ) : !correctionQuestions?.length ? (
            <Text fontSize="sm" color="var(--text-muted)" py={8} textAlign="center">
              No questions found for this submission.
            </Text>
          ) : (
            <Stack spacing={8}>
              {correctionQuestions.map((item, index) => {
                const question = getQuestionData(item);
                const questionId = getQuestionId(item);
                const maxMark = Number(question?.mark) || 0;
                const entry = corrections[questionId] ?? { mark: "", comment: "" };

                return (
                  <Box
                    key={questionId ?? index}
                    p={5}
                    borderWidth="1px"
                    borderColor="var(--border-subtle)"
                    borderRadius="xl"
                    bg="var(--surface-muted)"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="800"
                      color="var(--text-muted)"
                      textTransform="uppercase"
                      letterSpacing="0.1em"
                      mb={3}
                    >
                      Question {index + 1} · Max {maxMark} marks
                    </Text>

                    {question?.comprehension ? (
                      <Box mb={4}>
                        <Text fontSize="xs" fontWeight="700" color="var(--text-muted)" mb={1}>
                          Passage
                        </Text>
                        <Text fontSize="sm" color="var(--text-primary)" whiteSpace="pre-wrap">
                          {question.comprehension}
                        </Text>
                      </Box>
                    ) : null}

                    {question?.question ? (
                      <Box
                        mb={4}
                        fontSize="md"
                        color="var(--text-primary)"
                        sx={{
                          "& p, & span, & div, & li": { color: "inherit" },
                        }}
                        dangerouslySetInnerHTML={{ __html: question.question }}
                      />
                    ) : null}

                    {question?.image ? (
                      <Image
                        src={`${QUESTION_IMAGE_BASE}/${question.image}`}
                        alt={`Question ${index + 1}`}
                        maxW="100%"
                        maxH="280px"
                        objectFit="contain"
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor="var(--border-subtle)"
                        mb={4}
                      />
                    ) : null}

                    <Stack direction={{ base: "column", md: "row" }} spacing={4} align="flex-start">
                      <Box flex="0 0 120px">
                        <FormLabel fontSize="xs" fontWeight="700" mb={1}>
                          Mark
                        </FormLabel>
                        <Input
                          type="number"
                          min={0}
                          max={maxMark}
                          step="0.5"
                          value={entry.mark}
                          onChange={(e) => updateField(questionId, "mark", e.target.value)}
                          bg="var(--surface-raised)"
                          borderColor="var(--border-subtle)"
                          borderRadius="lg"
                        />
                      </Box>
                      <Box flex="1" w="100%">
                        <FormLabel fontSize="xs" fontWeight="700" mb={1}>
                          Comment
                        </FormLabel>
                        <Textarea
                          value={entry.comment}
                          onChange={(e) => updateField(questionId, "comment", e.target.value)}
                          placeholder="Feedback for this question (optional)"
                          minH="80px"
                          bg="var(--surface-raised)"
                          borderColor="var(--border-subtle)"
                          borderRadius="lg"
                        />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose} fontWeight="700">
            Cancel
          </Button>
          <Button
            bg={brandPalette.primary}
            color="white"
            fontWeight="700"
            onClick={handleSubmit}
            isLoading={isSubmittingCorrection}
            isDisabled={isQuestionsLoading || !correctionQuestions?.length}
            _hover={{ bg: brandPalette.secondary }}
          >
            Submit correction
          </Button>
        </ModalFooter>
      </ModalContent>

      <HomeworkPdfAnnotator
        isOpen={annotatorOpen}
        onClose={() => setAnnotatorOpen(false)}
        filename={studentPdfFilename}
        fetchPdfBytes={fetchExamPdfBytes}
        modalTitle="Annotate exam PDF"
        defaultFileName={studentPdfFilename || "exam.pdf"}
        loadErrorHint="Exam file endpoint not found. Add GET /api/admin/student_exams/file/{filename} on the server."
        onSave={setAnnotatedFile}
      />
    </Modal>
  );
};

export default CorrectExamModal;
